import { computed, ref } from 'vue';
import axios from 'axios';

// The catalogue of server locations, held in memory for the page load.
//
// Deliberately not in vuex: the store is persisted to sessionStorage wholesale,
// which made this a cache that survived every refresh and was never refetched,
// so a region renamed or disabled upstream stayed wrong for the life of the tab.
// `/server_locations` returns only the enabled ones, so a disabled region simply
// disappears -- there is no flag to notice.
const catalogue = ref([]);

export const serverLocations = computed(() => catalogue.value);

// A ping enriches a region that is already in the catalogue. It never adds or
// removes one, so a region nobody could reach simply keeps no ms.
export function setServerLatency(id, ms) {
  catalogue.value = catalogue.value.map((location) => (
    location.id === id ? { ...location, ms } : location
  ));
}

// What matchmaking picks a server location from. A region with no ms is left
// out rather than guessed.
export function measuredPings() {
  return Object.fromEntries(
    catalogue.value.filter((location) => location.ms != null).map((location) => [location.id, location.ms]),
  );
}

// No `X-TEAM-ID` header: the catalogue is the same for everyone, which is what
// makes one shared copy correct.
function fetchServerLocations() {
  return axios.get('/server_locations').then(({ data }) => {
    // Replaced wholesale, so an upstream rename or removal is honoured and the
    // pings that follow measure the list that just arrived.
    //
    // Checked for an array, not just null: a 204 or empty body reaches axios as
    // "", which every reader here would then call .map or .filter on.
    catalogue.value = Array.isArray(data) ? data : [];

    return catalogue.value;
  });
}

let loadRequest = null;

// Loads once per page load, so every picker can call it without a request each.
// Resolves with the catalogue and never rejects, so no caller has to catch.
export function loadServerLocations() {
  if (!loadRequest) {
    loadRequest = fetchServerLocations().catch((error) => {
      // A failed load is not a load: drop it so the next caller fetches again.
      loadRequest = null;
      console.log('Could not load server locations', error);

      return catalogue.value;
    });
  }

  return loadRequest;
}

// For the callers that hold a location from somewhere other than the catalogue
// -- a running server, a community server -- and only want its ping.
export function serverLatency(value) {
  return catalogue.value.find((location) => location.value === value)?.ms;
}

// A region shows a dash until its ping lands, and keeps it if it never does.
//
// Tested against null, not truthiness: a same-city PoP can measure 0, and that
// is a reading rather than a missing one.
export function latencyLabel(server) {
  return server?.ms == null ? '—' : `${server.ms}ms`;
}

// For a held choice, which is a snapshot taken when it was clicked and so does
// not pick up the ping that lands afterwards: read its ms off the catalogue.
export function latencyLabelFor(value) {
  return latencyLabel(catalogue.value.find((location) => location.value === value));
}

// Copied before sorting: sort() mutates in place and the catalogue is shared
// state. A region whose ping failed has no ms and sorts last rather than first.
export function sortByLatency(locations) {
  return [...(locations ?? [])].sort((a, b) => (a.ms ?? Infinity) - (b.ms ?? Infinity));
}
